import type { Course, Round } from "../types";
import { effectiveHolePars, effectiveStrokeIndexes } from "../golf";

/**
 * Resolve the effective course-for-scoring for a specific player in a round.
 * Prefers `round.player_tees[playerId]`, then the round's course.tee_name
 * (round-level default), then the raw course. When a tee is found, its
 * par / course_rating / slope_rating / hole_pars / stroke_indexes override
 * the course defaults.
 */
export function effectiveCourseForPlayer(round: Round, playerId: string): Course {
  const teeName = round.player_tees?.[playerId] ?? round.course.tee_name ?? null;
  if (!teeName || !round.course.tees || round.course.tees.length === 0) {
    return round.course;
  }
  const tee = round.course.tees.find((t) => t.name === teeName);
  if (!tee) return round.course;
  return {
    ...round.course,
    par: tee.par,
    course_rating: tee.course_rating,
    slope_rating: tee.slope_rating,
    hole_pars: tee.hole_pars ?? round.course.hole_pars,
    stroke_indexes: tee.stroke_indexes ?? round.course.stroke_indexes,
    tee_name: tee.name,
  };
}

/**
 * Per-player hole pars resolved from the player's assigned tee.
 * Falls back to the round-level default from golf.ts effectiveHolePars.
 *
 * NOTE: For TEAM scoring purposes (teamRoundPoints, teamContribution),
 * always use the round-level effectiveHolePars(round), NOT this function.
 * Team scoring uses the round's primary tee to determine par-3 vs par-4/5
 * rules consistently across all players, even when players are on different tees.
 */
export function effectiveHoleParsForPlayer(round: Round, playerId: string): number[] {
  const c = effectiveCourseForPlayer(round, playerId);
  if (c.hole_pars && c.hole_pars.length === 18) return c.hole_pars;
  return effectiveHolePars(round);
}

export function effectiveStrokeIndexesForPlayer(round: Round, playerId: string): number[] {
  const c = effectiveCourseForPlayer(round, playerId);
  if (c.stroke_indexes && c.stroke_indexes.length === 18) return c.stroke_indexes;
  return effectiveStrokeIndexes(round);
}
